import styled, { keyframes } from "styled-components";

// 상단에 fadeInUp 애니메이션 키프레임 추가
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(80px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// 애니메이션을 적용할 공통 스타일 컴포넌트 추가
export const FadeInSection = styled.div<{ delay?: number }>`
  opacity: 0;
  transform: translateY(80px);
  animation: ${fadeInUp} 1s ease-out forwards;
  animation-delay: ${props => props.delay || 0}s;
  will-change: opacity, transform;
  animation-play-state: paused;

  &.visible {
    animation-play-state: running;
  }
`;

export const HomeRoot = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  background-color: white;
  min-height: 100vh;
  height: auto;
  overflow-y: visible;
  overflow-x: hidden;
  position: relative;
  
  /* 스크롤바 숨기기 */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
  
  &::-webkit-scrollbar {
    display: none; /* Chrome, Safari, Opera */
  }
  
  .pc-only {
    display: block;
    
    @media (max-width: 1024px) {
      display: none;
    }
  }
  
  .mobile-only {
    display: none;
    
    @media (max-width: 1024px) {
      display: block;
    }
  }
  
  @media (max-width: 1024px) {
    height: auto;
    display: block;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    position: relative;
  }
`;

export const Wrap = styled.div`
  display: flex;
  justify-content: center;
  margin-left: 5rem;
  margin-right: 5rem;
`;

// 각 섹션 래퍼 컴포넌트 - 이 컴포넌트는 실제 애니메이션 효과 없이 공간만 차지
export const SectionWrapper = styled.div`
  width: 100%;
  position: relative;
  display: block;
  overflow: visible;
`;

export const ClientsSection = styled.section`
  width: 100%;
  padding: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
  opacity: 0;
  transform: translateY(0);
  
  &.visible {
    animation: ${fadeInUp} 1.2s ease-out forwards;
  }
  
  @media (max-width: 1024px) {
    padding: 0;
  }
`;

export const ClientsHeader = styled.div`
  display: flex;
  width: 100%;
  height: 100px;

    @media (max-width: 1024px) {
      height: 44px;
    }
`;

export const ClientsTitle = styled.div`
  width: 220px;
  height: 60px;
  margin: 20px 0;
  display: flex;
  justify-content: center;
  align-items: center;
  background: white;
  border-right: 2px #DCDCDC solid;
  flex-shrink: 0;
  
  > div {
    color: #999999;
    font-size: 24px;
    font-family: 'Montserrat', sans-serif;
    font-weight: 700;
    line-height: 40px;
    word-wrap: break-word;
  }

  @media (max-width: 1024px) {
    display: none;
  }
`;

export const ClientsContent = styled.div`
  display: none;
`;

// 무한 스크롤 애니메이션 정의
const scrollAnimation = keyframes`
  0% {
    transform: translate3d(0, 0, 0);
  }
  100% {
    transform: translate3d(-50.00%, 0, 0);
  }
`;

export const ClientsLogoContainer = styled.div`
  width: 100vw;
  margin-left: calc(-50vw + 50%);
  margin-right: calc(-50vw + 50%);
  overflow: hidden;
  height: 80px;
  padding: 20px 0;
  margin: 10px 0;
  position: relative;
  display: flex;
  align-items: center;
  box-sizing: border-box;
  
  @media (max-width: 1024px) {
    height: 44px;
    margin: 10px 0;
    display: flex;
    align-items: center;
  }
`;

export const LogoScrollWrapper = styled.div`
  display: flex;
  align-items: center;
  width: max-content;
  animation: ${scrollAnimation} 30s linear infinite;  // 15s에서 30s로 변경
  animation-timing-function: linear;
  animation-fill-mode: none;
  animation-iteration-count: infinite;
  height: 100%;
  gap: 56px;
  pointer-events: none;
  will-change: transform;
  transform: translate3d(0, 0, 0);
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
  -webkit-perspective: 1000;
  perspective: 1000;
  contain: layout style;
  
  &:hover {
    animation-play-state: paused;
  }
  
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
  
  @media (max-width: 1024px) {
    gap: 30px;
    height: 100%;
    animation: ${scrollAnimation} 25s linear infinite;  // 10s에서 25s로 변경
  }

  // JavaScript로 애니메이션 제어를 위해 애니메이션 제거 클래스 추가
  &.no-animation {
    animation: none;
  }
`;

export const LogoItem = styled.div`
  flex: 0 0 auto;
  width: auto;
  height: 60px;
  display: flex;
  justify-content: center;
  align-items: center;
  
  img {
    height: 100%;
    width: auto;  // width를 auto로 변경
    object-fit: contain;
    display: block;
    margin: auto;
  }
  
  @media (max-width: 1024px) {
    height: 44px;  // 고정된 높이 값으로 변경
    width: auto;
    
    img {
      height: 100%;
      width: auto;
      max-width: 120px;  // 최대 너비 제한
    }
  }
`;

// 서비스 섹션 스타일
export const ServiceSection = styled.section`
  width: 100%;
  height: 655px;
  padding-left: 0;
  padding-right: 0;
  margin-top: 220px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 20px;
  opacity: 0;
  transform: translateY(0);
  
  &.visible {
    animation: ${fadeInUp} 1.2s ease-out forwards;
  }
  
  @media (max-width: 1920px) {
    width: 100%;
    padding-left: 0;
    padding-right: 0;
    margin-bottom: calc(200px * 100vw / 1920);
  }

  @media (max-width: 1440px) {
    width: 100%;
    height: auto;
    padding-left: 0;
    padding-right: 0;
    margin-bottom: 150px;
  }

  @media (max-width: 1024px) {
    width: 100%;
    height: auto;
    margin-left: 0;
    margin-right: 0;
    margin-top: 100px;
    margin-bottom: 50px;
    padding-bottom: 40px;
    gap: 0px;
  }
`;

export const ServiceHeader = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 0;

  @media (max-width: 768px) {
    margin-bottom: 0;
    padding: 0px;
  }
`;

export const ServiceTitle = styled.div`
  color: #1D1D1D;
  font-size: 48px;
  font-family: 'Montserrat', sans-serif;
  font-weight: 700;
  line-height: 70px;
  word-wrap: break-word;

  @media (max-width: 768px) {
    font-size: 32px;
    line-height: 40px;
    padding: 0;
  }
`;

export const ServiceDescription = styled.div`
  color: #5C5C5C;
  font-size: 24px;
  font-family: 'Pretendard', sans-serif;
  font-weight: 400;
  line-height: 36px;
  word-wrap: break-word;
  white-space: normal;
  height: fit-content;
  margin-bottom: 40px;

  @media (max-width: 768px) {
    font-size: 16px;
    line-height: 24px;
    padding: 0;
    white-space: normal;
    word-break: keep-all;
    white-space: pre-wrap;
    margin-bottom: 0;
  }
`;

export const ServiceContent = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 48px;
  
  @media (max-width: 768px) {
    gap: 20px;
    margin-top: 0;
    position: relative;
    left: 20px;
  }
`;

export const ServiceTabs = styled.div`
  width: 100%;
  height: auto;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 50px;
  margin-bottom: 33px;

  @media (max-width: 768px) {
    display: none;
  }
`;

// 애니메이션 키프레임 추가
const slideInFromRight = keyframes`
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
`;

const slideOutToLeft = keyframes`
  from {
    transform: translateX(0);
    opacity: 1;
  }
  to {
    transform: translateX(-100%);
    opacity: 0;
  }
`;

// ServiceTab 컴포넌트 수정
export const ServiceTab = styled.div<{ active?: boolean; width?: number }>`
  width: ${props => props.width}px;
  height: 50px;
  padding-top: ${props => props.active ? '10px' : '16px'};
  padding-bottom: ${props => props.active ? '4px !important' : '16px'};
  border-bottom: ${props => props.active ? '3px #FF4F00 solid' : 'none'};
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  outline: none;
  -webkit-tap-highlight-color: transparent;
  user-select: none;
  box-sizing: border-box;
  
  &:focus, &:active {
    background-color: transparent;
    -webkit-tap-highlight-color: transparent;
    outline: none;
    padding-bottom: ${props => props.active ? '4px !important' : '16px'};
  }
  
  > div {
    color: ${props => props.active ? '#1D1D1D' : '#999999'};
    font-size: 24px;
    font-family: 'Pretendard', sans-serif;
    font-weight: 700;
    line-height: 36px;
    word-wrap: break-word;
    white-space: nowrap;
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

// ServiceTabContent 컴포넌트 수정
export const ServiceTabContent = styled.div<{ active?: boolean; direction?: string }>`
  width: 100%;
  max-width: 1440px;
  position: relative;
  display: flex;
  margin: 0 auto;
  
  /* 방향에 따른 애니메이션 적용 */
  animation: ${props => {
    if (props.direction === 'right') {
      return props.active ? slideInFromRight : slideOutToLeft;
    } else if (props.direction === 'left') {
      return props.active ? slideInFromLeft : slideOutToRight;
    }
    return 'none';
  }} 0.5s ease;
  
  @media (max-width: 1920px) {
    max-width: calc(1440px * 100vw / 1920);
  }

  @media (max-width: 768px) {
    width: calc(100vw - 20px);
    min-width: calc(100vw - 40px);
    flex-shrink: 0;
    scroll-snap-align: start;
    flex-direction: column;
    animation: none;
    background: transparent;
    position: relative;
    margin-left: 20px;
    overflow: visible;
    padding: 0;
    display: inline-flex;
    justify-content: center;
    align-items: center;
    margin: 0;
    box-sizing: border-box;
    padding-left: 20px;

    &:last-child {
      padding-right: 20px;
    }
  }
`;

// 반대 방향 애니메이션 추가
const slideInFromLeft = keyframes`
  from {
    transform: translateX(-100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
`;

const slideOutToRight = keyframes`
  from {
    transform: translateX(0);
    opacity: 1;
  }
  to {
    transform: translateX(100%);
    opacity: 0;
  }
`;

// ServiceTabBackground 컴포넌트 수정
export const ServiceTabBackground = styled.div`
  width: 100%;
  height: 396px;
  min-height: 396px;
  max-height: 396px;
  position: relative;
  background: #F8F8F8;
  border-radius: 28px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;

  @media (max-width: 768px) {
    padding: 20px 0 0 0;
    width: 100%;
    height: 250px;
    min-height: 250px;
    max-height: 250px;
    display: flex;
    justify-content: center;
    align-items: center;
    background: #F8F8F8;
    position: relative;
    overflow: visible;
    border-radius: 20px 20px 0 0;
    box-sizing: border-box;
  }
`;

export const ServiceTabImage = styled.div`
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  width: auto;
  height: auto;
  max-width: 35%;
  margin-right: 116px;
  
  img {
    width: auto;
    height: 100%;
    object-fit: contain;
    max-width: 100%;
  }
  
  @media (max-width: 1440px) {
    margin-right: 40px;
    height: 280px;
    max-width: 40%;
  }

  @media (max-width: 1280px) {
    margin-right: 30px;
    height: 260px;
    max-width: 40%;
  }

  @media (max-width: 1100px) {
    margin-right: 20px;
    height: 240px;
    max-width: 35%;
  }
  
  @media (max-width: 768px) {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    width: 215px;
    height: 180px;
    margin: 0;
    max-width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1;
    
    img {
      width: 100%;
      height: 100%;
      object-fit: contain;
      display: block;
      margin: 0 auto;
    }
  }
`;

// ServiceTabTextWrapper 컴포넌트 수정
export const ServiceTabTextWrapper = styled.div`
  @media (max-width: 768px) {
    align-self: stretch;
    width: 100%;
    height: 162px;
    padding: 20px 24px 20px 24px;
    background: white;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
    gap: 10px;
    box-sizing: border-box;
    text-align: left;
    border-radius: 0 0 20px 20px;
    margin-top: 0;
    position: relative;
    z-index: 2;
  }
`;

export const ServiceTabTitle = styled.div`
  color: #FF4F00;
  font-size: 24px;
  font-family: 'Montserrat', sans-serif;
  font-weight: 700;
  line-height: 36px;
  letter-spacing: -0.01em;
  word-wrap: break-word;
  margin-top: 100px;
  margin-left: 100px;

  @media (max-width: 768px) {
    margin: 0;
    align-self: stretch;
    color: #FF4F00;
    font-size: 16px;
    font-family: 'Montserrat', sans-serif;
    font-weight: 700;
    line-height: 28px;
    margin-right: 10px;
  }
`;

export const ServiceTabText = styled.div`
  color: #1D1D1D;
  font-size: 32px;
  font-family: 'Pretendard', sans-serif;
  font-weight: 400;
  line-height: 46px;
  letter-spacing: -0.01em;
  word-wrap: break-word;
  white-space: pre-wrap;
  margin-top: 16px;
  margin-left: 100px;
  max-width: 600px;

  @media (max-width: 1400px) {
    max-width: 45%;
    white-space: pre-wrap;
    word-break: keep-all;
  }

  @media (max-width: 768px) {
    margin: 0;
    align-self: stretch;
    height: auto;
    min-height: 84px;
    max-width: 100%;
    color: #1D1D1D;
    font-size: 16px;
    font-family: 'Pretendard', sans-serif;
    font-weight: 400;
    line-height: 26px;
    letter-spacing: -0.01em;
    white-space: pre-wrap;
    word-break: keep-all;
  }
`;

// Works 섹션 스타일
export const WorksSection = styled.section`
  width: 100%;
  height: auto;
  min-height: auto;
  padding-left: 0;
  padding-right: 0;
  margin-top: 300px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 100px;
  position: relative;
  opacity: 0;
  transform: translateY(0);
  
  &.visible {
    animation: ${fadeInUp} 1.2s ease-out forwards;
  }

  @media (max-width: 1920px) {
    margin-top: calc(300px * 100vw / 1920);
    gap: calc(100px * 100vw / 1920);
  }

  @media (max-width: 1440px) {
    margin-top: 200px;
    gap: 80px;
  }

  @media (max-width: 1024px) {
    margin-top: 90px;
    min-height: unset;
    padding-left: 0;
    padding-right: 0;
    gap: 30px;
  }

  @media (max-width: 768px) {
    margin-top: 60px;
  }
`;

export const WorksHeader = styled.div`
  width: 100%;
  position: relative;
  left: 0;
  top: 0;
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  
  @media (max-width: 1024px) {
    position: relative;
    width: 100%;
    margin-bottom: 10px;
    padding: 0 20px;
  }

  @media (max-width: 768px) {
    padding: 0;
  }
`;

export const WorksHeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  
  @media (max-width: 1024px) {
    width: 100%;
  }
`;

export const WorksTitle = styled.div`
  color: #1D1D1D;
  font-size: 48px;
  font-family: "T1", "Montserrat", sans-serif;
  font-weight: bold;
  line-height: 70px;
  word-wrap: break-word;
  
  @media (max-width: 1024px) {
    font-size: 32px;
    line-height: 40px;
  }
`;

export const WorksDescription = styled.div`
  color: #5C5C5C;
  font-size: 24px;
  font-family: 'Pretendard', sans-serif;
  font-weight: 400;
  line-height: 36px;
  word-wrap: break-word;
  word-break: keep-all;
  width: 100%;
  
  @media (max-width: 1920px) {
    font-size: calc(24px * 100vw / 1920);
    line-height: calc(36px * 100vw / 1920);
  }
  
  @media (max-width: 1024px) {
    font-size: 16px;
    font-weight: 400;
    line-height: 24px;
    margin: 0;
    width: 100%;
  }
`;

// 롤링 애니메이션 수정 - 완전히 끊김 없는 무한 롤링
const rollAnimation = keyframes`
  0% {
    transform: translate3d(0, 0, 0);
  }
  100% {
    transform: translate3d(calc(-400px * 10 - 30px * 9), 0, 0);
  }
`;

// WorksCarousel 컴포넌트 수정
export const WorksCarousel = styled.div`
  width: 100vw;
  position: relative;
  overflow-x: auto;
  overflow-y: hidden;
  margin-left: calc(-50vw + 50%);
  margin-right: calc(-50vw + 50%);
  scrollbar-width: none;
  -ms-overflow-style: none;
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  
  &::-webkit-scrollbar {
    display: none;
  }
  
  .carousel-container {
    display: flex;
    gap: 30px;
    overflow-x: visible;
    white-space: nowrap;
    scroll-snap-type: none;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    -ms-overflow-style: none;
    padding: 20px 0;
    margin: -20px 0;
    will-change: transform;
    transition: all 0.1s ease;
    user-select: none;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    scroll-behavior: smooth;
    
    &::-webkit-scrollbar {
      display: none;
    }
    
    /* Make it scrollable */
    cursor: grab;
    
    &:active {
      cursor: grabbing;
    }
    
    @media (max-width: 1920px) {
      gap: calc(30px * 100vw / 1920);
    }
    
    @media (max-width: 1024px) {
      gap: 16px;
      padding: 0;
      margin: 0;
      padding-right: 20px;
      box-sizing: border-box;
    }
  }
`;

// WorksCard 컴포넌트 수정
export const WorksCard = styled.div`
  width: 400px;
  flex-shrink: 0;
  cursor: pointer;
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  
  @media (min-width: 1025px) {
    &:hover {
      img {
        transform: scale(1.1);
      }
    }
  }

  @media (max-width: 1024px) {
    width: 247px;
    height: auto;
    flex-shrink: 0;
    box-sizing: border-box;
    overflow: hidden;
  }
`;

// WorksCardImage 컴포넌트 수정
export const WorksCardImage = styled.div`
  width: 100%;
  height: 500px;
  border-radius: 28px;
  overflow: hidden;
  box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.10);
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.5s ease;
    pointer-events: none;
    -webkit-user-drag: none;
    -khtml-user-drag: none;
    -moz-user-drag: none;
    -o-user-drag: none;
    user-drag: none;
  }
  
  ${WorksCard}:hover & img {
    transform: scale(1.15);
  }
  
  @media (max-width: 1920px) {
    height: calc(500px * 100vw / 1920);
  }
  
  @media (max-width: 1024px) {
    width: 247px;
    height: 309px;
    border-radius: 20px;
    margin: 0;
    
    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
    }
  }
`;

// WorksCardContent 컴포넌트 수정
export const WorksCardContent = styled.div`
  padding: 25px 0 0 0;
  display: flex;
  flex-direction: column;
  width: 100%;
  box-sizing: border-box;
  
  @media (max-width: 1920px) {
    padding: calc(24px * 100vw / 1920) 0 0 0;
  }
  
  @media (max-width: 1024px) {
    padding: 10px 0 10px 0;
    width: 247px;
    max-width: 100%;
  }
`;

export const WorksCardTitle = styled.div`
  color: #1D1D1D;
  font-size: 24px;
  font-family: 'Pretendard', sans-serif;
  font-weight: 600;
  line-height: 36px;
  word-wrap: break-word;
  margin-bottom: 16px;
  width: 100%;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  
  @media (max-width: 1920px) {
    font-size: calc(20px * 100vw / 1920);
    line-height: calc(32px * 100vw / 1920);
    margin-bottom: calc(16px * 100vw / 1920);
  }

  @media (max-width: 1024px) {
    font-size: 16px;
    margin-bottom: 4px;
    line-height: 28px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 247px;
    padding-right: 16px;
    box-sizing: border-box;
  }
`;

export const WorksCardTags = styled.div`
  display: flex;
  gap: 8px;
  width: 100%;
  overflow: hidden;
  white-space: nowrap;
  position: relative;

  @media (max-width: 1024px) {
    gap: 8px;
    width: 247px;
    max-width: 100%;
    padding-right: 16px;
    box-sizing: border-box;
  }
`;

export const WorksCardTag = styled.div`
  color: #999999;
  font-family: 'Montserrat';
  font-weight: 500;
  font-size: 16px;
  line-height: 28px;
  letter-spacing: -1%;
  white-space: nowrap;
  transition: color 0.3s ease;

  ${WorksCard}:hover & {
    color: #FF4F00;
  }

  &:not(:last-child) {
    flex-shrink: 0;
    &::after {
      content: "";
      margin-right: 8px;
    }
  }

  &:last-child {
    overflow: hidden;
    text-overflow: ellipsis;
    min-width: 0;
  }

  @media (max-width: 1024px) {
    color: #999999;
    font-size: 12px;
    line-height: 16px;
    letter-spacing: -0.01em;

    &:not(:last-child) {
      flex-shrink: 0;
      overflow: visible;
      text-overflow: unset;
    }

    &:last-child {
      overflow: hidden;
      text-overflow: ellipsis;
      min-width: 0;
    }
  }
`;

// PC 버전에서 컨텐츠를 가운데 정렬하기 위한 컨테이너
export const CenterContainer = styled.div`
  width: 100%;
  margin: 0 auto;
  padding: 0 240px;
  box-sizing: border-box;
  
  @media (max-width: 1920px) {
    padding: 0 240px;
  }
  
  @media (max-width: 1700px) {
    padding: 0 160px;
  }
  
  @media (max-width: 1440px) {
    padding: 0 80px;
  }
  
  @media (max-width: 768px) {
    padding: 0 20px;
    margin: 0;
  }
`;

// Power 섹션 스타일 수정
export const PowerSection = styled.section`
  width: 100%;
  padding: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 100px;
  position: relative;
  opacity: 0;
  transform: translateY(0);
  margin-top: 300px;
  
  &.visible {
    animation: ${fadeInUp} 1.2s ease-out forwards;
  }

  @media (max-width: 1920px) {
    gap: calc(100px * 100vw / 1920);
  }

  @media (max-width: 1024px) {
    gap: 40px;
    margin-top: 90px;
  }
`;

export const PowerHeader = styled.div`
  align-self: stretch;
  height: 116px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 10px;

  @media (max-width: 1024px) {
    height: auto;
  }
`;

export const PowerTitle = styled.div`
  color: #1D1D1D;
  font-size: 48px;
  font-family: 'Montserrat', sans-serif;
  font-weight: 700;
  line-height: 70px;
  word-wrap: break-word;

  @media (max-width: 1024px) {
    font-size: 32px;
    line-height: 40px;
  }
`;

export const PowerDescription = styled.div`
  color: #5C5C5C;
  font-size: 24px;
  font-family: 'Pretendard', sans-serif;
  font-weight: 400;
  line-height: 36px;
  word-wrap: break-word;

  @media (max-width: 1024px) {
    font-size: 16px;
    font-weight: 400;
    line-height: 24px;
    margin-top: 0;
    word-break: keep-all;
  }
`;

export const PowerContent = styled.div`
  align-self: stretch;
  width: 100%;
  height: auto;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 60px;
  
  @media (max-width: 1920px) {
    gap: calc(60px * 100vw / 1920);
  }

  @media (max-width: 1024px) {
    width: 100%;
    gap: 0px;
  }
  
  @media (max-width: 480px) {
    gap: 0px;
  }
`;

export const PowerStatsRow = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  gap: 20px;
  
  @media (max-width: 1024px) {
    flex-wrap: wrap;
    justify-content: space-between;
    gap: 15px;
  }
  
  @media (max-width: 480px) {
    flex-wrap: wrap;
    justify-content: space-between;
    gap: 15px;
    width: 100%;
    margin-bottom: 0;
  }
`;

export const PowerStatCard = styled.div<{ bgColor: string }>`
  width: 345px;
  height: 345px;
  position: relative;
  background: ${props => props.bgColor};
  box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.10);
  border-radius: 28px;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
  
  @media (max-width: 1920px) {
    width: calc(345px * 100vw / 1920);
    height: calc(345px * 100vw / 1920);
  }

  @media (max-width: 1024px) {
    width: calc(50% - 7.5px);
    height: auto;
    aspect-ratio: 1;
    border-radius: 16px;
  }
  
  @media (max-width: 480px) {
    width: calc(50% - 7.5px);
    height: auto;
    aspect-ratio: 1;
    margin: 0;
    border-radius: 16px;
  }
`;

export const PowerStatImage = styled.img`
  position: absolute;
  width: auto;
  height: auto;
  left: 43px;
  top: 35px;
  max-width: 354px;
  max-height: 354px;
  object-fit: contain;
  
  @media (max-width: 1920px) {
    left: calc(43px * 100vw / 1920);
    top: calc(35px * 100vw / 1920);
    max-width: calc(354px * 100vw / 1920);
    max-height: calc(354px * 100vw / 1920);
  }
  
  @media (max-width: 1024px) {
    left: 10%;
    top: 10%;
    max-width: 80%;
    max-height: 80%;
  }
  
  @media (max-width: 480px) {
    left: auto;
    right: -10%;
    bottom: -10%;
    top: auto;
    max-width: 90%;
    max-height: 90%;
  }
`;

export const PowerStatContent = styled.div`
  position: absolute;
  width: 100%;
  height: auto;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 2;
  
  @media (max-width: 1920px) {
    /* 미디어 쿼리 내용 유지 */
  }
`;

export const PowerNumber = styled.div`
  color: white;
  font-size: 72px;
  font-family: 'Montserrat', sans-serif;
  font-weight: 700;
  line-height: 84px;
  word-wrap: break-word;
  
  @media (max-width: 1920px) {
    font-size: calc(72px * 100vw / 1920);
    line-height: calc(84px * 100vw / 1920);
  }
  
  @media (max-width: 1024px) {
    font-size: 72px;
    line-height: 84px;
  }
  
  @media (max-width: 480px) {
    font-size: 48px;
    line-height: 56px;
  }
`;

export const PowerLabel = styled.div`
  color: white;
  font-size: 24px;
  font-family: 'Pretendard', sans-serif;
  font-weight: 500;
  line-height: 36px;
  word-wrap: break-word;
  
  @media (max-width: 1920px) {
    font-size: calc(24px * 100vw / 1920);
    line-height: calc(36px * 100vw / 1920);
  }
  
  @media (max-width: 1024px) {
    font-size: 24px;
    line-height: 36px;
    padding: 0 5px;
  }
  
  @media (max-width: 480px) {
    font-size: 16px;
    line-height: 24px;
    padding: 0;
    opacity: 0.7;
  }
`;

export const PowerPartnersRow = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;
  margin-top: 0;
  
  @media (max-width: 1920px) {
    gap: 20px;
    margin-top: 0;
  }

  @media (max-width: 1440px) {
    gap: 15px;
  }

  @media (max-width: 1100px) {
    display: flex;
    justify-content: center;
    width: 100%;
    gap: 15px;
    margin-top: 40px;
    flex-wrap: nowrap;
    padding: 0 20px;
    box-sizing: border-box;
  }
`;

export const PowerPartnerCard = styled.div`
  width: 380px;
  height: 120px;
  padding: 16px 20px;
  border-radius: 16px;
  border: 1px #DCDCDC solid;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 3px;
  box-sizing: border-box;
  overflow: hidden;
  position: relative;
  
  @media (max-width: 1920px) {
    width: 380px;
    height: 120px;
    padding: 16px 20px;
    gap: 3px;
  }

  @media (max-width: 1440px) {
    width: 320px;
    height: 110px;
    padding: 14px 18px;
  }

  @media (max-width: 1280px) {
    width: 280px;
    height: 100px;
    padding: 12px 16px;
  }

  @media (max-width: 1100px) {
    width: 160px;
    height: 160px;
    padding: 15px;
    border-radius: 16px;
    outline: 1px #DCDCDC solid;
    outline-offset: -1px;
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    gap: 5px;
    align-items: center;
    overflow: hidden;
    box-sizing: border-box;
  }

  @media (max-width: 768px) {
    width: 130px;
    height: 130px;
    padding: 12px;
  }

  @media (max-width: 480px) {
    width: calc(100px + (115 - 100) * ((100vw - 320px) / (445 - 320)));
    height: calc(100px + (115 - 100) * ((100vw - 320px) / (445 - 320)));
    padding: 10px;
    min-width: 100px;
    min-height: 100px;
  }
`;

export const PowerPartnerLogo = styled.img<{ partnerId?: number }>`
  width: auto;
  height: ${props => {
    if (props.partnerId === 1) return '50px';
    if (props.partnerId === 2) return '60px';
    return '72px';
  }};
  object-fit: contain;
  margin-bottom: 10px;
  padding: 0;
  object-position: ${props => props.partnerId === 2 ? 'center center' : 'center'};
  transform: ${props => props.partnerId === 2 ? 'scale(1.0)' : 'none'};
  margin-top: 8px;
  
  @media (max-width: 1920px) {
    height: ${props => {
      if (props.partnerId === 1) return '50px';
      if (props.partnerId === 2) return '60px';
      return '72px';
    }};
  }

  @media (max-width: 1440px) {
    height: ${props => {
      if (props.partnerId === 1) return '45px';
      if (props.partnerId === 2) return '55px';
      return '65px';
    }};
    margin-bottom: ${props => props.partnerId === 2 ? '12px' : '8px'};
  }

  @media (max-width: 1280px) {
    height: ${props => {
      if (props.partnerId === 1) return '40px';
      if (props.partnerId === 2) return '50px';
      return '60px';
    }};
    margin-bottom: ${props => props.partnerId === 2 ? '10px' : '6px'};
  }

  @media (max-width: 1100px) {
    width: auto;
    height: 90px;
    margin: 5px 0 0 0;
    max-width: 90%;
    object-fit: contain;
    object-position: center;
    transform: none;
  }

  @media (max-width: 768px) {
    height: 70px;
  }

  @media (max-width: 480px) {
    height: 55px;
  }
`;

export const PowerPartnerLabel = styled.div<{ partnerId?: number }>`
  align-self: stretch;
  text-align: center;
  color: #999999;
  font-size: 16px;
  font-family: 'Pretendard', sans-serif;
  font-weight: 500;
  line-height: 28px;
  word-wrap: break-word;
  position: relative;
  top: ${props => {
    if (props.partnerId === 1) return '-10px';
    if (props.partnerId === 2) return '-10px';
    if (props.partnerId === 3) return '-10px';
    return '0';
  }};
  
  @media (max-width: 1920px) {
    font-size: calc(16px * 100vw / 1920);
    line-height: calc(28px * 100vw / 1920);
  }

  @media (max-width: 1440px) {
    font-size: 14px;
    line-height: 24px;
    top: ${props => {
      if (props.partnerId === 1) return '-6px';
      if (props.partnerId === 2) return '-8px';
      if (props.partnerId === 3) return '-8px';
      return '0';
    }};
  }

  @media (max-width: 1280px) {
    font-size: 13px;
    line-height: 20px;
    top: ${props => {
      if (props.partnerId === 1) return '-6px';
      if (props.partnerId === 2) return '-6px';
      if (props.partnerId === 3) return '-6px';
      return '0';
    }};
  }

  @media (max-width: 1100px) {
    font-size: 12px;
    line-height: 16px;
    width: 100%;
    max-height: 32px;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    position: static;
    top: 0;
  }

  @media (max-width: 768px) {
    font-size: 11px;
    line-height: 14px;
  }

  @media (max-width: 480px) {
    font-size: 9px;
    line-height: 12px;
  }
`;

// Trust 섹션 스타일
export const TrustSection = styled.section`
  width: 100%;
  height: auto;
  min-height: auto;
  position: relative;
  background: #F8F8F8;
  margin-top: 300px;
  padding-top: 120px;
  padding-bottom: 0;
  opacity: 0;
  transform: translateY(0);
  
  &.visible {
    animation: ${fadeInUp} 1.2s ease-out forwards;
  }

  @media (max-width: 1024px) {
    margin-top: 130px;
    padding: 60px 0 0 0;
  }
  
  @media (max-width: 480px) {
    padding-bottom: 0;
    min-height: 0;
  }
`;

export const TrustHeader = styled.div`
  height: 116px;
  position: relative;
  left: 0;
  top: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 100px;
  
  @media (max-width: 1920px) {
    margin-bottom: calc(100px * 100vw / 1920);
  }

  @media (max-width: 1024px) {
    position: relative;
    top: 0;
    width: 100%;
    margin-bottom: 0;
    padding-left: 0;
    padding-right: 0;
    gap: 0;
    height: auto;
  }
  
  @media (max-width: 480px) {
    gap: 0;
    height: auto;
  }
`;

export const TrustTitle = styled.div`
  color: #1D1D1D;
  font-size: 48px;
  font-family: 'Montserrat', sans-serif;
  font-weight: 700;
  line-height: 70px;
  word-wrap: break-word;
  margin-bottom: 10px;

  @media (max-width: 1024px) {
    font-size: 32px;
    line-height: 40px;
    margin-bottom: 10px;
    padding-bottom: 0;
  }
`;

export const TrustDescription = styled.div`
  color: #5C5C5C;
  font-size: 24px;
  font-family: 'Pretendard', sans-serif;
  font-weight: 500;
  line-height: 36px;
  word-wrap: break-word;

  @media (max-width: 1024px) {
    font-size: 16px;
    font-weight: 400;
    line-height: 24px;
    margin-top: 0;
    margin-bottom: 0;
    padding-top: 0;
  }
`;

// Trust 섹션 롤링 애니메이션 정의
const trustRollAnimation = keyframes`
  0% {
    transform: translate3d(0, 0, 0);
  }
  100% {
    transform: translate3d(-50.00%, 0, 0);
  }
`;

// 모바일용 왼쪽으로 움직이는 애니메이션 추가
const trustMobileRollAnimation = keyframes`
  0% {
    transform: translate3d(0, 0, 0);
  }
  100% {
    transform: translate3d(-50.00%, 0, 0);
  }
`;

// TrustCarousel 컴포넌트 수정
export const TrustCarousel = styled.div`
  position: relative;
  width: 100vw;
  left: 50%;
  right: 50%;
  margin-left: -50vw;
  margin-right: -50vw;
  overflow-x: auto;
  overflow-y: hidden;
  padding-bottom: 120px;
  -webkit-font-smoothing: antialiased;
  contain: layout style paint;
  scrollbar-width: none;
  -ms-overflow-style: none;
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  
  &::-webkit-scrollbar {
    display: none;
  }
  
  .trust-roll-container {
    display: flex;
    gap: 25px;
    padding-left: 5%;
    width: max-content;
    will-change: transform;
    transition: all 0.1s ease;
    cursor: grab;
    
    &:active {
      cursor: grabbing;
    }
  }
  
  @media (max-width: 1920px) {
    padding-bottom: calc(120px * 100vw / 1920);
  }

  @media (max-width: 1024px) {
    width: 100vw;
    left: 50%;
    right: 50%;
    margin-left: -50vw;
    margin-right: -50vw;
    padding-bottom: 80px;
    overflow-x: auto;
    margin-top: 40px;
    
    .trust-roll-container {
      gap: 20px;
      padding-left: 20px;
      width: max-content;
    }
  }
`;

export const TrustCard = styled.div`
  width: 467px !important;
  height: 370px !important;
  max-width: 467px !important;
  background: white;
  border-radius: 24px;
  box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.05);
  padding: 30px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 24px;
  flex-shrink: 0;
  transition: box-shadow 0.3s ease, transform 0.3s ease;
  box-sizing: border-box;
  will-change: transform;
  transform: translate3d(0, 0, 0);
  contain: layout style;
  cursor: pointer;
  
  &:hover {
    box-shadow: 0px 8px 30px rgba(0, 0, 0, 0.1);
  }
  
  &:active {
    transform: scale(0.98);
  }
  
  @media (max-width: 1920px) {
    width: calc(467px * 100vw / 1920) !important;
    max-width: calc(467px * 100vw / 1920) !important;
    padding: calc(30px * 100vw / 1920);
  }

  @media (max-width: 1024px) {
    width: 300px !important;
    height: 500px !important;
    max-width: 300px !important;
    min-width: 300px !important;
    min-height: 400px !important;
    padding: 30px;
    border-radius: 24px;
    gap: 12px;
    display: flex;
    flex-direction: column;
  }
`;

export const TrustCardLogo = styled.img`
  width: 120px !important;
  height: 40px !important;
  object-fit: contain;
  padding: 0;
  align-self: flex-start;
  margin-bottom: 0;
  
  &.larger-logo {
    width: 120px !important;
    height: 40px !important;
    object-position: left center;
    max-width: 120px !important;
  }
  
  &.logo-goldenblue,
  &.logo-tiktok,
  &.logo-hyeum,
  &.logo-burgerking,
  &.logo-cleanbedding {
    height: 40px !important;
    margin-top: 0;
    margin-bottom: 0;
    transform: none;
    transform-origin: left center;
    object-position: left center;
  }
  
  @media (max-width: 1920px) {
    width: calc(120px * 100vw / 1920) !important;
    height: calc(40px * 100vw / 1920) !important;
  }

  @media (max-width: 1024px) {
    width: 120px !important;
    height: 40px !important;
    margin-bottom: 0px;
    object-position: left center;
  }
  
  @media (max-width: 480px) {
    width: 120px !important;
    height: 40px !important;
    margin-bottom: 0;
    margin-top: 0;
    object-position: left center;
    
    &.logo-goldenblue,
    &.logo-tiktok,
    &.logo-hyeum,
    &.logo-burgerking,
    &.logo-cleanbedding {
      width: 120px !important;
      height: 40px !important;
      transform: none;
      object-position: left center;
    }
  }
`;

export const TrustCardQuote = styled.div`
  color: #1D1D1D;
  font-family: 'Pretendard', sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 26px;
  letter-spacing: -1%;
  word-wrap: break-word;
  margin: 0;
  width: 407px;
  flex: 1;
  position: relative;
  overflow: visible;
  white-space: normal;
  
  @media (max-width: 1920px) {
    font-size: calc(16px * 100vw / 1920);
    line-height: calc(26px * 100vw / 1920);
    width: calc(407px * 100vw / 1920);
  }
  
  @media (max-width: 1024px) {
    font-size: 16px;
    line-height: 26px;
    width: 100%;
    flex: 1;
    height: auto;
  }
  
  @media (max-width: 480px) {
    font-family: 'Pretendard', sans-serif;
    font-weight: 400;
    font-size: 16px;
    line-height: 26px;
    letter-spacing: -1%;
    width: 100%;
    flex: 1;
    height: auto;
  }
`;

export const TrustCardAuthor = styled.div`
  color: #1D1D1D;
  font-family: 'Pretendard', sans-serif;
  font-size: 16px;
  font-weight: 500;
  line-height: 28px;
  letter-spacing: -1%;
  word-wrap: break-word;
  margin-top: 0;
  width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  
  @media (max-width: 1920px) {
    font-size: calc(16px * 100vw / 1920);
    line-height: calc(28px * 100vw / 1920);
  }
  
  @media (max-width: 1024px) {
    font-size: 16px;
    line-height: 28px;
    margin-top: 0;
  }
  
  @media (max-width: 480px) {
    font-family: 'Pretendard', sans-serif;
    font-weight: 500;
    font-size: 16px;
    line-height: 28px;
    letter-spacing: -1%;
    margin-top: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    width: 100%;
  }
`;

// Contact 섹션 스타일
export const ContactSection = styled.section`
  width: 100%;
  height: 450px;
  position: relative;
  background: #1D1D1D;
`;

export const ContactTitle = styled.div`
  left: 240px;
  top: 97px;
  position: absolute;
  color: white;
  font-size: 48px;
  line-height: 70px;
  word-wrap: break-word;
  
  @media (max-width: 1920px) {
    left: calc(120px * 100vw / 1920);
    top: calc(97px * 100vw / 1920);
    font-size: calc(48px * 100vw / 1920);
    line-height: calc(70px * 100vw / 1920);
  }
`;

export const ContactButtons = styled.div`
  left: 240px;
  top: 287px;
  position: absolute;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 14px;
  
  @media (max-width: 1920px) {
    left: calc(120px * 100vw / 1920);
    top: calc(287px * 100vw / 1920);
    gap: calc(14px * 100vw / 1920);
  }
`;

export const ContactButton = styled.div`
  width: 160px;
  height: 60px;
  padding: 16px 0;
  background: #FF4F00;
  border-radius: 50px;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  box-sizing: border-box;
  transition: background-color 0.3s ease;
  
  &:hover {
    background: #E64600;
  }
  
  > div {
    color: white;
    font-size: 16px;
    font-family: 'Pretendard', sans-serif;
    font-weight: 700;
    line-height: 28px;
    word-wrap: break-word;
    text-align: center;
    white-space: nowrap;
  }
  
  @media (max-width: 1920px) and (min-width: 1025px) {
    width: 160px;
    height: 60px;
    
    a {
      width: 160px;
      height: 60px;
      line-height: 60px;
      font-size: 16px;
    }
  }
  
  @media (max-width: 1024px) {
    &.pc-only {
      display: none;
    }
    
    &.mobile-only {
      display: flex;
      width: calc(100% - 40px);
      margin: 40px 20px;
      padding: 0;
      box-sizing: border-box;
      
      a {
        width: 100%;
        height: 60px;
        line-height: 60px;
        font-size: 16px;
      }
    }
  }
`;

export const DownloadButton = styled.div`
  width: 200px;
  height: 60px;
  padding: 16px 20px;
  background: white;
  border-radius: 50px;
  border: 1px #DCDCDC solid;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  box-sizing: border-box;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: #FF4F00;
    
    > div {
      color: #FF4F00;
    }
  }
  
  > div {
    color: #1D1D1D;
    font-size: 16px;
    font-family: 'Pretendard', sans-serif;
    font-weight: 500;
    line-height: 28px;
    word-wrap: break-word;
    text-align: center;
    white-space: nowrap;
    transition: color 0.3s ease;
  }
  
  @media (max-width: 1920px) {
    width: calc(200px * 100vw / 1920);
    height: calc(60px * 100vw / 1920);
    padding: calc(16px * 100vw / 1920) calc(20px * 100vw / 1920);
  }
`;

export const ContactGraphic = styled.div`
  position: absolute;
  right: 0;
  top: 0;
  width: 1010px;
  height: 450px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  overflow: hidden;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    transition: transform 0.5s ease;
    
    &:hover {
      transform: scale(1.1);
    }
  }
  
  @media (max-width: 1920px) {
    width: calc(1010px * 100vw / 1920);
    height: calc(450px * 100vw / 1920);
  }
`;

// 배너 관련 스타일 컴포넌트 수정
export const BannerContainer = styled.div`
  width: 100%;
  height: 100vh;
  max-height: 1080px;
  background-color: #000000;
  display: flex;
  justify-content: flex-start;
  align-items: flex-end;
  padding: 0 240px;
  box-sizing: border-box;
  position: relative;
  filter: brightness(1.1) contrast(1.1);
  overflow: hidden;
  z-index: 1;
  
  @media (max-width: 1920px) {
    padding: 0 calc(240px * 100vw / 1920);
    height: 100vh;
    min-height: 700px;
  }
  
  @media (max-width: 1440px) {
    padding: 0 80px;
  }
  
  @media (max-width: 1024px) {
    padding: 0 40px;
    height: 100vh;
    max-height: 609px;
    min-height: auto;
    position: relative;
    overflow: visible;
    z-index: 1;
    background-position: center top;
  }
  
  @media (max-width: 768px) {
    padding: 0 20px;
  }

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
    z-index: 1;
  }

  > div {
    position: relative;
    z-index: 2;
    width: 100%;
    max-width: 1440px;
    margin: 0;
    margin-left: 0;
    margin-right: auto;

    @media (max-width: 1024px) {
      text-align: left;
    }
  }

  video {
    object-fit: cover;
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    z-index: 0;
  }
`;

export const BannerTitle = styled.h1`
  color: white;
  font-size: 58px;
  line-height: 80px;
  font-family: "Montserrat", sans-serif;
  font-weight: 700;
  letter-spacing: -0.01em;
  word-wrap: break-word;
  margin-top: 0;
  margin-bottom: 0;
  max-width: 90%;
  text-align: left;
  
  @media (max-width: 1920px) {
    font-size: calc(72px * 100vw / 1920);
    line-height: calc(96px * 100vw / 1920);
    letter-spacing: -0.01em;
  }

  @media (max-width: 1600px) {
    font-size: 58px;
    line-height: 72px;
  }

  @media (max-width: 1024px) {
    font-size: 48px;
    line-height: 64px;
    max-width: 100%;
    letter-spacing: -0.01em;
    br {
      display: inline;
    }
  }

  @media (max-width: 768px) {
    margin-top: 329px;
    font-size: 32px;
    line-height: 40px;
    letter-spacing: -0.01em;
  }

  @media (max-width: 480px) {
    margin-top: 329px;
    font-size: 32px;
    line-height: 40px;
    letter-spacing: -0.01em;
  }
`;

export const MoreButton = styled.div`
  width: 225px;
  height: 225px;
  padding: 28px 20px;
  background: #1d1d1d;
  display: inline-flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  margin-top: 40px;
  margin-bottom: calc(120px * 100vh / 1080);
  cursor: pointer;
  transition: background-color 0.3s ease;
  position: relative;
  box-sizing: border-box;
  
  &:hover {
    background: #333333;
  }
  
  @media (max-width: 1920px) {
    padding: calc(48px * 100vw / 1920) calc(32px * 100vw / 1920);
    margin-top: calc(40px * 100vw / 1920);
    margin-bottom: calc(120px * 100vh / 1080);
  }

  @media (max-width: 1024px) {
    width: 122px;
    height: 122px;
    padding: 20px;
    margin: 40px 0 0;
  }

  @media (max-width: 768px) {
    width: 122px;
    height: 122px;
    padding: 20px;
    margin: 38px 0 0;
  }

  @media (max-width: 480px) {
    width: 122px;
    height: 122px;
    padding: 8px 20px 20px 12px;
    margin: 38px 0 0;
  }
`;

export const MoreText = styled.div`
  color: white;
  font-size: 32px;
  font-family: "Montserrat", sans-serif;
  font-weight: 700;
  line-height: 40px;
  word-wrap: break-word;
  
  @media (max-width: 1920px) {
    font-size: calc(32px * 100vw / 1920);
    line-height: calc(40px * 100vw / 1920);
  }

  @media (max-width: 1024px) {
    font-size: 16px;
    line-height: 28px;
  }

  @media (max-width: 768px) {
    font-size: 16px;
    line-height: 28px;
  }

  @media (max-width: 480px) {
    font-size: 16px;
    line-height: 28px;
  }
`;

export const SvgWrapper = styled.div`
  position: relative;
  margin-top: 26px;
  
  svg {
    @media (max-width: 1920px) {
      width: calc(22px * 100vw / 1920);
      height: calc(41px * 100vw / 1920);
    }

    @media (max-width: 1024px) {
      width: 18px;
      height: 34px;
      margin-top: 0px;
    }

    @media (max-width: 768px) {
      width: 16px;
      height: 30px;
      margin-top: 0px;
    }

    @media (max-width: 480px) {
      width: 14px;
      height: 26px;
      margin-top: 0px;
    }
  }

  @media (max-width: 1024px) {
    margin-top: 10px;
  }
`;

// 서비스 카드 슬라이더 컨테이너 추가
export const ServiceCardSlider = styled.div`
  display: none;
  
  @media (max-width: 768px) {
    display: block;
    width: 100vw;
    margin-left: calc(-50vw + 50% - 20px);
    margin-right: calc(-50vw + 50%);
    overflow: visible;
    position: relative;
    padding-left: 0;
    margin-top: 0;
  }
`;

export const ServiceCardContainer = styled.div`
  display: none;
  
  @media (max-width: 768px) {
    display: flex;
    transition: transform 0.3s ease;
    width: 100%;
    margin: 0;
    padding: 46px 20px 50px 20px;
    box-sizing: border-box;
    overflow-x: auto;
    scroll-snap-type: x mandatory;
    -webkit-overflow-scrolling: touch;
    scroll-behavior: smooth;
    will-change: scroll-position;
    gap: 0;
    
    &::-webkit-scrollbar {
      display: none;
    }
    -ms-overflow-style: none;
    scrollbar-width: none;
    
    -webkit-backface-visibility: hidden;
    -webkit-perspective: 1000;
    backface-visibility: hidden;
    perspective: 1000;
    transform: translate3d(0, 0, 0);
  }
`;

export const PageIndicator = styled.div`
  display: none;
  
  @media (max-width: 768px) {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 10px;
    margin-top: 10px;
    padding-bottom: 30px;
    className: "page-indicator";
  }
`;

export const IndicatorDot = styled.div<{ active: boolean }>`
  width: 32px;
  height: 3px;
  background-color: ${props => props.active ? '#FF4F00' : '#DCDCDC'};
  transition: background-color 0.3s ease;
  cursor: pointer;
  className: "indicator-dot";
`;

// PC 버전 컨테이너 스타일 추가
export const ServicePCContent = styled.div`
  position: relative;
  width: 100%;
  height: 396px;
  min-height: 396px;
  max-height: 396px;
  margin-top: 0;
  margin-bottom: 100px;

  @media (max-width: 1920px) {
    margin-bottom: calc(100px * 100vw / 1920);
  }

  @media (max-width: 1440px) {
    margin-bottom: 80px;
  }

  @media (max-width: 768px) {
    display: none;
    margin-bottom: 0;
  }
`;

// ViewMoreButton 컴포넌트 수정
export const ViewMoreButton = styled.div`
  width: 160px;
  height: 60px;
  padding: 0;
  border-radius: 50px;
  border: 1px #DCDCDC solid;
  display: flex;
  justify-content: center;
  align-items: center;
  box-sizing: border-box;
  cursor: pointer;
  
  a {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 160px;
    height: 60px;
    padding: 0;
    box-sizing: border-box;
    text-align: center;
    color: #1D1D1D;
    font-size: 16px;
    font-family: 'Pretendard', sans-serif;
    font-weight: 500;
    line-height: 60px;
    white-space: nowrap;
    transition: color 0.3s ease;
    text-decoration: none;
    border-radius: 50px;
  }
  
  &:hover {
    a {
      color: #FF4F00;
    }
  }
  
  @media (max-width: 1920px) and (min-width: 1025px) {
    width: 160px;
    height: 60px;
    
    a {
      width: 160px;
      height: 60px;
      line-height: 60px;
      font-size: 16px;
    }
  }

  @media (max-width: 1024px) {
    &.pc-only {
      display: none;
    }
    
    &.mobile-only {
      display: flex;
      width: 100%;
      height: 52px;
      margin: 0px 0px 40px 0px;
      padding: 0;
      box-sizing: border-box;
      
      a {
        width: 100%;
        height: 52px;
        line-height: 52px;
      }
    }
  }
`;

// 새로운 내부 카드 컨테이너 추가
export const ServiceCardInner = styled.div`
  @media (max-width: 768px) {
    width: 100%;
    height: 406px;
    min-height: 406px;
    max-height: 406px;
    background: transparent;
    border-radius: 20px;
    box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.10);
    outline: 1px solid #F0F0F0;
    border: none;
    overflow: visible;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: center;
    position: relative;
    margin: 0;
    padding: 0;
  }
`;